import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, PAGE_SEO, buildCanonicalUrl, type PageSEO } from './seo';
import seoRoutes from './seoRoutes.json';
import { privateEquityAiEntitySignals } from './marketingAeo';

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/images/returncatalyst-logo.png`,
    description:
      'ReturnCatalyst is a private equity AI platform that helps automate deal diligence from CIM upload through IC memo generation and portfolio monitoring. PE firms use it to extract CIM data, build financial models, run AI investment committee simulations, generate cited IC memos, and monitor portfolio KPIs in structured dashboards.',
    foundingDate: '2025',
    slogan: 'AI-powered deal operations for private equity',
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Private Equity Firms',
    },
    knowsAbout: privateEquityAiEntitySignals.map((signal) => signal.term),
    sameAs: ['https://www.linkedin.com/company/returncatalyst'],
    parentOrganization: {
      '@type': 'Organization',
      name: 'Otomat',
      url: 'https://otomat.ai',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@returncatalyst.ai',
      contactType: 'sales',
    },
  };
}

export function webSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description:
      'ReturnCatalyst is a private equity AI platform that helps automate deal diligence from CIM upload through IC memo generation and portfolio monitoring. PE firms use it to extract CIM data, build financial models, run AI investment committee simulations, generate cited IC memos, and monitor portfolio KPIs in structured dashboards.',
    inLanguage: 'en-US',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
  };
}

// Mirrors cleanTitle() in scripts/prerender-seo-routes.mjs so a page's schema
// name matches the prerendered one exactly. The suffix is the literal brand the
// titles in seoRoutes.json are authored with rather than SITE_NAME, because
// SITE_NAME follows the serving hostname while those titles do not — matching on
// SITE_NAME would leave the suffix attached on the other hosts that serve this
// same corpus. Blog titles end " | ReturnCatalyst Blog", so this leaves a
// trailing "Blog" on them, which is what the prerenderer emits too.
function schemaPageName(title: string): string {
  return title.replace(' | ReturnCatalyst', '');
}

/** Mirrors splitKeywords() in scripts/prerender-seo-routes.mjs. */
function keywordThings(keywords?: string) {
  return (keywords || '')
    .split(',')
    .map((term) => term.trim())
    .filter(Boolean)
    .map((term) => ({ '@type': 'Thing', name: term }));
}

/**
 * WebPage node for one indexed marketing page, deliberately field-for-field
 * with webPageNode() in scripts/prerender-seo-routes.mjs. The prerendered @graph
 * is authoritative on a fresh load and this is what stands in for it after a
 * client-side route change, so the two have to assert the same facts about the
 * page rather than two different versions of them.
 *
 * isPartOf/publisher are inlined rather than the prerenderer's @id references
 * because these nodes ship as standalone script blocks: an @id pointing into
 * another block would dangle.
 */
export function webPageSchema(seo: PageSEO) {
  const url = buildCanonicalUrl(seo.canonical);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: schemaPageName(seo.title),
    description: seo.description,
    // The prerenderer stamps the default OG card on every WebPage node, even on
    // routes that carry a per-route og:image, so this does the same.
    image: DEFAULT_OG_IMAGE,
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    about: keywordThings(seo.keywords),
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Private Equity Firms',
    },
  };
}

export function softwareApplicationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Private Equity Deal Operations',
    operatingSystem: 'Web',
    description:
      'ReturnCatalyst is a private equity AI platform that helps automate deal diligence from CIM upload through IC memo generation and portfolio monitoring. PE firms use it to extract CIM data, build financial models, run AI investment committee simulations, generate cited IC memos, and monitor portfolio KPIs in structured dashboards.',
    url: SITE_URL,
    sameAs: ['https://www.linkedin.com/company/returncatalyst'],
    screenshot: `${SITE_URL}/images/og/og-default.jpg`,
    featureList: [
      'CIM analysis with source-linked outputs in minutes',
      '8-persona IC Committee simulation with voting and Chairman synthesis',
      '24-section IC memo generation with two-tier synthesis architecture',
      'Sector research with TAM/SAM/SOM sizing and competitive landscape',
      'M&A transaction discovery across public sources',
      'Financial model generation with formula reverse engineering',
      '37 PE-specific legal document templates with AI drafting',
      'Portfolio monitoring from the latest successfully processed reporting',
      'Conversational AI with RAG retrieval and inline citations',
      'IC presentation PPTX generation from 7 data sources',
      'Due diligence questionnaire generation with citation backing',
      'Voice intelligence with document-grounded answers',
      'Cited web research using external sources retrieved during the run',
      'Deal pipeline management with stage tracking',
      'Meeting intelligence integration (Granola, Otter.ai)',
      'SharePoint/OneDrive document synchronization',
    ].join(', '),
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      offerCount: 3,
      offers: [
        {
          '@type': 'Offer',
          name: 'GP Platform',
          description: 'AI deal operations for the deal team',
        },
        {
          '@type': 'Offer',
          name: 'GP + Portfolio',
          description: 'Platform plus portfolio company AI operations',
        },
        {
          '@type': 'Offer',
          name: 'Enterprise',
          description: 'Full platform with custom integrations and dedicated support',
        },
      ],
    },
    creator: {
      '@type': 'Organization',
      name: 'Otomat',
      url: 'https://otomat.ai',
    },
    keywords:
      'private equity AI, AI due diligence, CIM analysis software, IC memo automation, AI financial modeling, portfolio monitoring AI, investment committee simulation',
  };
}

export function productSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: SITE_NAME,
    description:
      'ReturnCatalyst is a private equity AI platform that helps automate deal diligence from CIM upload through IC memo generation and portfolio monitoring. PE firms use it to extract CIM data, build financial models, run AI investment committee simulations, generate cited IC memos, and monitor portfolio KPIs in structured dashboards.',
    url: SITE_URL,
    image: `${SITE_URL}/images/og/og-default.jpg`,
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    category: 'Private Equity Software',
    keywords:
      'private equity AI, PE deal operations, AI due diligence, CIM analysis, IC memo automation, portfolio monitoring',
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Private Equity Firms',
    },
    isRelatedTo: privateEquityAiEntitySignals.map((signal) => ({
      '@type': 'Thing',
      name: signal.term,
      description: signal.description,
    })),
  };
}

export function serviceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Otomat AI Operations for Private Equity',
    description:
      'Outsourced Chief AI Officer services for PE firms. Otomat embeds as your AI operations team, providing the ReturnCatalyst platform plus custom AI tooling, portfolio company AI deployments, and ongoing optimization.',
    provider: {
      '@type': 'Organization',
      name: 'Otomat',
      url: 'https://otomat.ai',
    },
    serviceType: 'AI Consulting & Software',
    areaServed: 'Worldwide',
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Private Equity General Partners',
    },
  };
}

export function offerCatalogSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    name: 'ReturnCatalyst Private Equity AI Platform Tiers',
    url: `${SITE_URL}/pricing`,
    itemListElement: [
      {
        '@type': 'Offer',
        name: 'GP Platform',
        description:
          'AI deal operations for private equity deal teams, including CIM analysis, sector research, transaction discovery, financial modeling, IC simulation, and IC memo automation.',
        url: `${SITE_URL}/pricing`,
      },
      {
        '@type': 'Offer',
        name: 'GP + Portfolio',
        description:
          'Private equity AI platform access plus portfolio company AI operations, KPI monitoring, variance alerts, and portfolio analytics.',
        url: `${SITE_URL}/pricing`,
      },
      {
        '@type': 'Offer',
        name: 'Enterprise',
        description:
          'Full private equity AI platform with custom integrations, dedicated support, and firm-specific workflow calibration.',
        url: `${SITE_URL}/pricing`,
      },
    ],
  };
}

export function definedTermSetSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: 'Private Equity AI Concepts Covered by ReturnCatalyst',
    url: SITE_URL,
    hasDefinedTerm: privateEquityAiEntitySignals.map((signal, index) => ({
      '@type': 'DefinedTerm',
      position: index + 1,
      name: signal.term,
      description: signal.description,
      inDefinedTermSet: SITE_URL,
    })),
  };
}

export function privateEquityUseCaseItemListSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'ReturnCatalyst Private Equity AI Use Cases',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'AI due diligence for private equity deal teams',
        url: `${SITE_URL}/use-cases/deal-teams`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Investment committee simulation and IC memo automation',
        url: `${SITE_URL}/use-cases/managing-partners`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Portfolio monitoring AI for private equity operations',
        url: `${SITE_URL}/use-cases/portfolio-ops`,
      },
    ],
  };
}

export function howToSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Analyze a Deal with ReturnCatalyst',
    description:
      'Use ReturnCatalyst\'s One-Button Full Analysis Pipeline to go from CIM upload to IC-ready materials in approximately 20 minutes.',
    totalTime: 'PT20M',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Upload CIM',
        text: 'Upload a Confidential Information Memorandum (PDF, DOCX, PPTX, or Excel). The platform automatically extracts financial data, company overview, and key metrics.',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Run Full Analysis Pipeline',
        text: 'Click one button to trigger the full pipeline. ReturnCatalyst runs CIM analysis, then sector research and transaction search in parallel, followed by IC Committee simulation and IC memo generation.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Review IC Committee Simulation',
        text: 'Review analyses from 8 AI expert personas (CFO Advisor, Deal Partner, Industry Expert, Exit Strategist, Credit Analyst, Operating Partner, General Counsel, AI/Tech Disruptor) with a Chairman synthesis and voting recommendation.',
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Review IC Memo',
        text: 'Review the auto-generated 24-section IC memorandum with source-linked financial analysis, risk assessment, and investment recommendation aligned to committee sentiment.',
      },
      {
        '@type': 'HowToStep',
        position: 5,
        name: 'Generate IC Presentations',
        text: 'Generate IC presentation slide decks from PE-specific templates, all pre-populated with deal data from the analysis.',
      },
    ],
  };
}

export function faqPageSchema(
  faqs: Array<{ question: string; answer: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function blogPostSchema(post: {
  title: string;
  description: string;
  slug: string;
  date: string;
  author: string;
  ogImage?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    url: `${SITE_URL}/blog/${post.slug}`,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: post.author,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/images/returncatalyst-logo.png`,
      },
    },
    image: post.ogImage || `${SITE_URL}/images/og/og-default.jpg`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${post.slug}`,
    },
  };
}

export function breadcrumbSchema(
  items: Array<{ name: string; url: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

/**
 * Combined structured data for the landing page.
 *
 * The FAQPage is built from seoRoutes.json `homeFaqs` — the same list the
 * homepage renders through ObjectionFAQ and the same list the prerenderer puts
 * in the static @graph. It must not be sourced from anywhere else: an FAQPage
 * describing questions the page does not display is both a Google FAQ policy
 * violation and a direct contradiction of the prerendered block this stands in
 * for.
 */
export function landingPageSchema() {
  return [
    webSiteSchema(),
    webPageSchema(PAGE_SEO['/']),
    organizationSchema(),
    softwareApplicationSchema(),
    productSchema(),
    serviceSchema(),
    offerCatalogSchema(),
    definedTermSetSchema(),
    privateEquityUseCaseItemListSchema(),
    faqPageSchema(seoRoutes.homeFaqs.map((faq) => ({ question: faq.q, answer: faq.a }))),
    howToSchema(),
  ];
}

/**
 * The schema every indexed marketing page below the homepage declares: its own
 * WebPage node plus the Home → page breadcrumb, matching the WebPage +
 * BreadcrumbList pair the prerenderer emits for these routes.
 *
 * Every indexed page must supply structured data of its own. SEOHead treats the
 * prerendered static @graph as authoritative only for the URL it was
 * prerendered for and removes it after a client-side route change, so a page
 * that passes nothing ends up with no structured data at all once a visitor has
 * navigated away and back.
 */
export function marketingPageSchema(seo: PageSEO) {
  return [
    webPageSchema(seo),
    breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: schemaPageName(seo.title), url: seo.canonical },
    ]),
  ];
}
